import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (s): { next?: string } => ({
    next: typeof s.next === 'string' ? s.next : undefined,
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loginMut = useMutation({
    mutationFn: () => api.authLogin({ email: email.trim().toLowerCase(), password }),
    onSuccess: async (user) => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      toast.success(`Welcome back, ${user.email}`);
      const next = search.next && search.next.startsWith('/') ? search.next : '/';
      navigate({ to: next });
    },
    onError: (e) => {
      const msg = String(e);
      if (msg.includes('401')) setError('Invalid email or password.');
      else setError(msg);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }
    loginMut.mutate();
  }

  return (
    <div className="mx-auto mt-16 max-w-md">
      <div className="rounded-lg border border-border bg-card p-8 shadow-sm">
        <img
          src="/scribe-logo-animated.svg"
          alt="Scribe"
          className="mb-6 h-10 w-auto"
        />
        <p className="text-sm text-muted-foreground">
          Enter your email and password to continue.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loginMut.isPending}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60"
          >
            {loginMut.isPending && <Spinner className="size-4 text-primary-foreground" />}
            Sign in
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-muted-foreground">
          New here?{' '}
          <Link to="/register" className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}
